/**
 * One invite suggestion under the share search (SE-8313): avatar + name,
 * pressed to pick the person.
 * @module features/scouting/components/molecules/ShareInviteRow/ShareCandidateRow/ShareCandidateRowView
 */

import { memo } from "react";

import { Avatar, AvatarFallbackText } from "@/components/atoms/Avatar";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import { TEST_ID_WATCHLIST_SHARE } from "e2e/testId";

/** Props for {@link ShareCandidateRowView}. */
export interface ShareCandidateRowViewProps {
  /** User id (test identity). */
  readonly userId: string;
  /** Display name. */
  readonly name: string;
  /** Press handler. */
  readonly onPress: () => void;
}

/**
 * Renders one suggestion row.
 * @param root0 - View props
 * @param root0.userId - User id
 * @param root0.name - Display name
 * @param root0.onPress - Press handler
 * @returns The rendered row.
 */
const ShareCandidateRowView = ({
  userId,
  name,
  onPress,
}: ShareCandidateRowViewProps) => (
  <Pressable
    testID={TEST_ID_WATCHLIST_SHARE.candidateFor(userId)}
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={`Invite ${name}`}
    className="web:hover:bg-surface-raised flex-row items-center gap-3 px-3 py-2"
  >
    <Avatar size="sm" className="bg-surface-muted">
      <AvatarFallbackText>{name}</AvatarFallbackText>
    </Avatar>
    <Text variant="body" className="text-content-primary" numberOfLines={1}>
      {name}
    </Text>
  </Pressable>
);

ShareCandidateRowView.displayName = "ShareCandidateRowView";

export default memo(ShareCandidateRowView);
